import type { jsPDF } from 'jspdf';
import type { ObservationAgreementSignatureDetail } from '../api/types';
import {
  colors,
  contentWidth,
  drawFooters,
  drawRule,
  drawSectionBar,
  encodable,
  filenameSlug,
  measure,
  page,
  PdfCursor,
  setFill,
  setText,
  writeParagraph,
} from '../pdf/pdfKit';

/**
 * Renders one signed observation agreement as a PDF — the agreement as it was worded for that
 * person, then what they ticked, the name they signed with and the moment it was recorded.
 *
 * This is the copy of the record that leaves the application: printed for a file, attached to an
 * email, handed to whoever asks what somebody agreed to. So it carries everything the database row
 * carries and invents nothing — the clauses come from the snapshot taken at signing, not from the
 * agreement as it reads today, and the API has already filled the signer's name into the blank.
 *
 * Built in the browser for the reason the assessment export documents at length: Azure App Service
 * for Linux ships almost no fonts, so a server-rendered page of text would risk missing glyphs,
 * while the browser has fonts and jsPDF's standard faces come from the PDF viewer. jsPDF is loaded
 * through a dynamic import at the call site, so it only downloads when somebody exports.
 *
 * The shared page geometry, colours, cursor and character folding are in `../pdf/pdfKit`.
 */

const style = {
  paragraph: { size: 10.5, lineHeight: 15, gapAfter: 10 },
  listIntro: { size: 10.5, lineHeight: 15, gapAfter: 6 },
  bullet: { size: 10.5, lineHeight: 15, indent: 22, gapAfter: 5 },
  panel: { paddingX: 11, paddingY: 9, radius: 4, bar: 2.5, size: 10, lineHeight: 15 },
  field: { labelSize: 8, valueSize: 11, gap: 3, gapAfter: 12 },
} as const;

function drawParagraph(cursor: PdfCursor, text: string): void {
  writeParagraph(cursor, text, {
    size: style.paragraph.size,
    lineHeight: style.paragraph.lineHeight,
    color: colors.ink,
  });

  cursor.y += style.paragraph.gapAfter;
}

/**
 * One bullet, with the text hanging beside the glyph rather than wrapping back under it — the same
 * shape the `<ul>` has on screen, and the shape that keeps a three-line undertaking readable as one
 * item instead of three.
 */
function drawBullet(cursor: PdfCursor, text: string): void {
  const { doc } = cursor;
  const lines = measure(doc, text, contentWidth - style.bullet.indent, style.bullet.size, false);

  // Reserved whole where it fits on a page at all: splitting one undertaking across a page break is
  // avoidable here in a way that splitting a long answer is not.
  cursor.reserve(lines.length * style.bullet.lineHeight);

  setText(doc, colors.green);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(style.bullet.size);
  doc.text('•', page.margin + 6, cursor.y + style.bullet.size);

  setText(doc, colors.ink);

  lines.forEach((line, index) => {
    doc.text(line, page.margin + style.bullet.indent, cursor.y + style.bullet.size + index * style.bullet.lineHeight);
  });

  cursor.y += lines.length * style.bullet.lineHeight + style.bullet.gapAfter;
}

/** The mint panel with the green accent down its left edge, as the answer panel has on screen. */
function drawQuotePanel(cursor: PdfCursor, text: string): void {
  const { doc } = cursor;
  const textWidth = contentWidth - style.panel.bar - style.panel.paddingX * 2;
  const lines = measure(doc, text, textWidth, style.panel.size, false);
  const height = style.panel.paddingY * 2 + lines.length * style.panel.lineHeight;

  cursor.reserve(height);

  setFill(doc, colors.mint);
  doc.roundedRect(page.margin, cursor.y, contentWidth, height, style.panel.radius, style.panel.radius, 'F');

  // Square, not rounded: on screen the accent runs the full height of the block.
  setFill(doc, colors.green);
  doc.rect(page.margin, cursor.y, style.panel.bar, height, 'F');

  setText(doc, colors.ink);
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(style.panel.size);

  lines.forEach((line, index) => {
    doc.text(
      line,
      page.margin + style.panel.bar + style.panel.paddingX,
      cursor.y + style.panel.paddingY + style.panel.size + index * style.panel.lineHeight,
    );
  });

  cursor.y += height;
}

/**
 * A labelled value — the paper form's ruled boxes, which have no reason to be boxes here.
 *
 * A field the signer left blank is still drawn, as a greyed "Not given". Dropping it would make the
 * exported copy look like a form that never asked, which is a different document from the one they
 * were shown.
 */
function drawField(
  cursor: PdfCursor,
  label: string,
  value: string | null,
  options?: { emphasis?: boolean },
): void {
  const { doc } = cursor;

  cursor.reserve(style.field.labelSize + style.field.gap + style.field.valueSize + style.field.gapAfter);

  setText(doc, colors.muted);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(style.field.labelSize);
  doc.text(encodable(label.toUpperCase()), page.margin, cursor.y + style.field.labelSize);

  cursor.y += style.field.labelSize + style.field.gap;

  // The signed name is set larger and in italics — it is a signature, and on a page of plain fields
  // it should be the one thing that reads as having been written by a person.
  const size = options?.emphasis ? style.field.valueSize + 2 : style.field.valueSize;

  setText(doc, value === null ? colors.muted : colors.ink);
  doc.setFont('helvetica', value === null ? 'italic' : options?.emphasis ? 'bolditalic' : 'normal');
  doc.setFontSize(size);
  doc.text(encodable(value ?? 'Not given'), page.margin, cursor.y + size);

  cursor.y += size + style.field.gapAfter;
}

/** Draws the whole document. Separate from saving it so it can be exercised outside a browser. */
export function renderAgreementPdf(doc: jsPDF, signature: ObservationAgreementSignatureDetail): void {
  const cursor = new PdfCursor(doc);
  const signed = new Date(signature.signedAtUtc);

  writeParagraph(cursor, signature.organization.toUpperCase(), {
    size: 8,
    lineHeight: 11,
    bold: true,
    color: colors.muted,
  });
  cursor.y += 2;
  writeParagraph(cursor, signature.agreementTitle, {
    size: 16,
    lineHeight: 20,
    bold: true,
    color: colors.ink,
  });

  cursor.y += 10;
  drawRule(cursor);
  cursor.y += 16;

  for (const clause of signature.clauses) {
    if (clause.kind === 'listItem') {
      drawBullet(cursor, clause.text);
      continue;
    }

    if (clause.kind === 'listIntro') {
      writeParagraph(cursor, clause.text, {
        size: style.listIntro.size,
        lineHeight: style.listIntro.lineHeight,
        color: colors.ink,
      });
      cursor.y += style.listIntro.gapAfter;
      continue;
    }

    drawParagraph(cursor, clause.text);
  }

  cursor.y += 2;
  writeParagraph(cursor, signature.attribution, { size: 8, lineHeight: 11, color: colors.muted });

  cursor.y += 20;

  // The acceptance, set apart from the text it was given for — and the reason this export exists at
  // all, since the clauses above are the same for everybody and this block is not.
  drawSectionBar(cursor, 'Signed');

  drawQuotePanel(cursor, `“${signature.consentStatement}”`);
  cursor.y += 16;

  drawField(cursor, 'Signature', signature.signerName, { emphasis: true });
  drawField(cursor, 'Email', signature.email);
  drawField(cursor, 'Clinic', signature.location);
  drawField(
    cursor,
    'Date',
    `${signed.toLocaleDateString()} ${signed.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`,
  );

  // Which edition was signed. Small, and last, because it means nothing to most readers and
  // everything to the one reader comparing two signatures taken either side of a revision.
  writeParagraph(cursor, `Agreement version ${signature.agreementVersion}`, {
    size: 8,
    lineHeight: 11,
    color: colors.muted,
  });

  drawFooters(doc, signature.signerName);
}

/**
 * A filename someone can find again in a downloads folder: who signed, and when. The date is ISO so
 * a folder of these sorts chronologically rather than by whatever the locale put first.
 */
export function agreementPdfFilename(signature: ObservationAgreementSignatureDetail): string {
  const slug = filenameSlug(signature.signerName);
  const date = signature.signedAtUtc.slice(0, 10);

  return `observation-agreement-${slug || 'signed'}-${date}.pdf`;
}

/** Builds the PDF and hands it to the browser as a download. */
export async function exportAgreementPdf(signature: ObservationAgreementSignatureDetail): Promise<void> {
  const { jsPDF: JsPdf } = await import('jspdf');

  const doc = new JsPdf({ unit: 'pt', format: 'a4', compress: true });
  renderAgreementPdf(doc, signature);
  doc.save(agreementPdfFilename(signature));
}
